import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  Search as SearchIcon,
  FilterAlt as FilterAltIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import ExportProgressDialog, { ExportProgressState } from '../../components/shared/ExportProgressDialog';
import PDFViewer from '../../components/shared/PDFViewer';
import api from '../../services/api';
import SearchableSelect from '../../components/SearchableSelect';
import CarteraSearch from '../../components/CarteraSearch';
import type { Cartera } from '../../services/carteraService';
import { useNavigate } from 'react-router-dom';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import { formatNroChequeExcel, formatNroChequeVista } from '../../utils/nroCheque';
import { getAccessToken } from '../../utils/sessionAuthStore';
import { sortByField } from '../../utils/sortByField';
import type { ChequesSortDir } from '../../services/tesoreriaService';

type ChequePropioSortKey =
  | 'nroCheque'
  | 'nombreBanco'
  | 'nombreSucursal'
  | 'nroLibrador'
  | 'librador'
  | 'importe'
  | 'fechaIngreso'
  | 'fechaVencimiento'
  | 'nroCuenta';

interface ChequesPropiosFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
  nroBanco?: string;
  /** @deprecated usar carteraNombre */
  bancoNombre?: string;
  carteraNombre?: string;
  nroSucursal?: string;
  fechaVencimientoDesde?: string;
  fechaVencimientoHasta?: string;
  fechaIngresoDesde?: string;
  fechaIngresoHasta?: string;
  importeDesde?: string;
  importeHasta?: string;
}

interface ChequePropio {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: number;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroDestino: number;
  fechaVencimiento: string;
  concepto: string;
  nroCuenta: string | null;
}

interface SucursalBanco {
  nroBanco: number;
  nroSucursal: number;
  sucursal: string;
}

const extractChequesPropiosResponse = (
  data: any
): { items: ChequePropio[]; totalCount: number; totalPages: number } => {
  if (data?.chequesPropios && Array.isArray(data.chequesPropios)) {
    const totalCount = data.totalCount ?? data.chequesPropios.length;
    return {
      items: data.chequesPropios,
      totalCount,
      totalPages: data.totalPages ?? Math.max(1, Math.ceil(totalCount / (data.pageSize || 20))),
    };
  }
  if (data?.items && Array.isArray(data.items)) {
    const totalCount = data.totalCount ?? data.items.length;
    return {
      items: data.items,
      totalCount,
      totalPages: data.totalPages ?? Math.max(1, Math.ceil(totalCount / (data.pageSize || 20))),
    };
  }
  if (Array.isArray(data)) {
    return { items: data, totalCount: data.length, totalPages: 1 };
  }
  return { items: [], totalCount: 0, totalPages: 1 };
};

const filterChequesPorImporte = <T extends { importe: number }>(
  items: T[],
  importeDesde: string,
  importeHasta: string
): T[] => {
  const min = importeDesde.trim() !== '' && !isNaN(parseFloat(importeDesde))
    ? parseFloat(importeDesde)
    : null;
  const max = importeHasta.trim() !== '' && !isNaN(parseFloat(importeHasta))
    ? parseFloat(importeHasta)
    : null;
  if (min == null && max == null) return items;
  return items.filter((cheque) => {
    const importe = cheque.importe ?? 0;
    if (min != null && importe < min) return false;
    if (max != null && importe > max) return false;
    return true;
  });
};

/** Evita que Excel convierta identificadores largos a notación científica. */
const excelTexto = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const aplicarColumnasTextoExcel = (ws: XLSX.WorkSheet, columnasTexto: string[]) => {
  if (!ws['!ref']) return;

  const range = XLSX.utils.decode_range(ws['!ref']);
  const headerRow = range.s.r;
  const columnas = new Set(columnasTexto);

  for (let c = range.s.c; c <= range.e.c; c++) {
    const headerCell = ws[XLSX.utils.encode_cell({ r: headerRow, c })];
    const header = headerCell?.v;
    if (typeof header !== 'string' || !columnas.has(header)) continue;

    for (let r = headerRow + 1; r <= range.e.r; r++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = ws[ref];
      if (!cell || cell.v === null || cell.v === undefined || cell.v === '') continue;
      cell.t = 's';
      cell.v = String(cell.v);
      cell.z = '@';
      delete cell.w;
    }
  }
};

const ChequesPropios: React.FC = () => {
  const loadStored = () =>
    loadTesoreriaFiltros<ChequesPropiosFiltrosStored>(TESORERIA_FILTROS_KEYS.chequesPropios);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [cheques, setCheques] = useState<ChequePropio[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 20));
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [order, setOrder] = useState<ChequesSortDir>('asc');
  const [orderBy, setOrderBy] = useState<ChequePropioSortKey | ''>('');
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Filtros
  const [sucursales, setSucursales] = useState<SucursalBanco[]>([]);
  const [nroBanco, setNroBanco] = useState(() => storedString(loadStored(), 'nroBanco', ''));
  const [carteraNombre, setCarteraNombre] = useState(() => {
    const stored = loadStored();
    return storedString(stored, 'carteraNombre', '') || storedString(stored, 'bancoNombre', '');
  });
  const [nroSucursal, setNroSucursal] = useState(() => storedString(loadStored(), 'nroSucursal', ''));
  const [fechaVencimientoDesde, setFechaVencimientoDesde] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoDesde', '')
  );
  const [fechaVencimientoHasta, setFechaVencimientoHasta] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoHasta', '')
  );
  const [fechaIngresoDesde, setFechaIngresoDesde] = useState(() =>
    storedString(loadStored(), 'fechaIngresoDesde', '')
  );
  const [fechaIngresoHasta, setFechaIngresoHasta] = useState(() =>
    storedString(loadStored(), 'fechaIngresoHasta', '')
  );
  const [importeDesde, setImporteDesde] = useState(() => storedString(loadStored(), 'importeDesde', ''));
  const [importeHasta, setImporteHasta] = useState(() => storedString(loadStored(), 'importeHasta', ''));
  const [carteraSearchOpen, setCarteraSearchOpen] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgressState>({
    message: '',
    currentPage: 0,
    totalPages: 0,
  });
  const resetExportProgress = () =>
    setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.chequesPropios,
    {
      searchTerm,
      rowsPerPage,
      nroBanco,
      carteraNombre,
      nroSucursal,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      fechaIngresoDesde,
      fechaIngresoHasta,
      importeDesde,
      importeHasta,
    },
    [
      searchTerm,
      rowsPerPage,
      nroBanco,
      carteraNombre,
      nroSucursal,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      fechaIngresoDesde,
      fechaIngresoHasta,
      importeDesde,
      importeHasta,
    ]
  );

  // Cargar sucursales
  useEffect(() => {
    const loadSucursales = async () => {
      try {
        const token = getAccessToken();
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };
        const sucursalesResponse = await api.get('/api/Tesoreria/sucursales-banco', { headers });
        setSucursales(sucursalesResponse.data || []);
      } catch (error: any) {
        console.error('Error al cargar sucursales:', error);
      }
    };
    loadSucursales();
  }, []);

  const buildFilterParams = (pageNumber: number, pageSize: number) => {
    const params: Record<string, string | number> = {
      pageNumber,
      pageSize,
    };

    if (searchTerm.trim()) {
      params.searchTerm = searchTerm.trim();
    }
    if (nroBanco) {
      params.nroBanco = parseInt(nroBanco, 10);
    }
    if (nroSucursal) {
      params.nroSucursal = parseInt(nroSucursal, 10);
    }
    if (fechaVencimientoDesde) {
      params.fechaVencimientoDesde = fechaVencimientoDesde;
    }
    if (fechaVencimientoHasta) {
      params.fechaVencimientoHasta = fechaVencimientoHasta;
    }
    // El API usa FechaDesde/FechaHasta para fecha de ingreso
    if (fechaIngresoDesde) {
      params.fechaDesde = fechaIngresoDesde;
    }
    if (fechaIngresoHasta) {
      params.fechaHasta = fechaIngresoHasta;
    }
    if (orderBy) {
      params.sortBy = orderBy;
      params.sortDir = order;
    }

    return params;
  };

  useEffect(() => {
    const hasImporteFilter =
      (importeDesde.trim() !== '' && !isNaN(parseFloat(importeDesde))) ||
      (importeHasta.trim() !== '' && !isNaN(parseFloat(importeHasta)));

    const loadData = async () => {
      try {
        setLoading(true);
        const token = getAccessToken();
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };
        const pageSize = Math.min(rowsPerPage, 20);

        // ImporteDesde/Hasta: el API de propios no los acepta; filtramos en cliente.
        if (hasImporteFilter) {
          const allCheques: ChequePropio[] = [];
          let pageNumber = 1;
          let totalPages = 1;

          do {
            const response = await api.get('/api/Tesoreria/cheques-propios', {
              headers,
              params: buildFilterParams(pageNumber, 20),
            });
            const { items, totalCount, totalPages: pages } = extractChequesPropiosResponse(response.data);
            allCheques.push(...items);
            totalPages = pages || Math.max(1, Math.ceil(totalCount / 20));
            if (!items.length) break;
            pageNumber += 1;
          } while (pageNumber <= totalPages && pageNumber <= 100);

          const filtered = filterChequesPorImporte(allCheques, importeDesde, importeHasta);
          const sorted = orderBy ? sortByField(filtered, orderBy, order) : filtered;
          const maxPage = Math.max(0, Math.ceil(sorted.length / pageSize) - 1);
          const effectivePage = Math.min(page, maxPage);
          setTotalCount(sorted.length);
          setCheques(sorted.slice(effectivePage * pageSize, effectivePage * pageSize + pageSize));
          if (effectivePage !== page) {
            setPage(effectivePage);
          }
          return;
        }

        const response = await api.get('/api/Tesoreria/cheques-propios', {
          headers,
          params: buildFilterParams(page + 1, pageSize),
        });
        const { items, totalCount } = extractChequesPropiosResponse(response.data);
        setCheques(items);
        setTotalCount(totalCount);
      } catch (error: any) {
        console.error('Error al obtener los cheques propios:', error);
        enqueueSnackbar(
          error.response?.data?.message || 'No se pudieron cargar los cheques propios.',
          { variant: 'error' }
        );
        setCheques([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() || hasImporteFilter ? 500 : 0);

    return () => clearTimeout(timer);
  }, [
    searchTerm,
    page,
    rowsPerPage,
    nroBanco,
    nroSucursal,
    fechaVencimientoDesde,
    fechaVencimientoHasta,
    fechaIngresoDesde,
    fechaIngresoHasta,
    importeDesde,
    importeHasta,
    order,
    orderBy,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const handleClosePdfPreview = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Math.min(parseInt(event.target.value, 10), 20);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleRequestSort = (property: ChequePropioSortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleCarteraSelect = (cartera: Cartera) => {
    setNroBanco(String(cartera.nroBanco));
    setCarteraNombre(`${cartera.nroBanco} - ${cartera.nombreBanco?.trim() || ''}`);
    setNroSucursal('');
    setPage(0);
    setCarteraSearchOpen(false);
  };

  const handleClearFilters = () => {
    setNroBanco('');
    setCarteraNombre('');
    setNroSucursal('');
    setFechaVencimientoDesde('');
    setFechaVencimientoHasta('');
    setFechaIngresoDesde('');
    setFechaIngresoHasta('');
    setImporteDesde('');
    setImporteHasta('');
    setSearchTerm('');
    setPage(0);
  };

  const sucursalesFiltradas = nroBanco
    ? sucursales.filter(s => s.nroBanco === parseInt(nroBanco))
    : [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const fetchExportItems = async (
    onProgress?: (currentPage: number, totalPages: number) => void
  ): Promise<{ items: ChequePropio[]; apiTotalCount: number; rawFetchedCount: number }> => {
    const token = getAccessToken();
    const headers = { Authorization: token ? `Bearer ${token}` : undefined };
    const pageSize = 20;
    const allItems: ChequePropio[] = [];
    let pageNumber = 1;
    let apiTotalCount = 0;

    while (pageNumber <= 500) {
      const response = await api.get('/api/Tesoreria/cheques-propios', {
        headers,
        params: buildFilterParams(pageNumber, pageSize),
      });
      const { items, totalCount, totalPages } = extractChequesPropiosResponse(response.data);
      if (totalCount > 0) apiTotalCount = totalCount;

      if (!items.length) {
        onProgress?.(Math.max(pageNumber - 1, 1), pageNumber > 1 ? pageNumber - 1 : 1);
        break;
      }

      allItems.push(...items);

      const fetchedAll = apiTotalCount > 0 && allItems.length >= apiTotalCount;
      const isLastPage = items.length < pageSize || (totalPages > 0 && pageNumber >= totalPages);

      if (fetchedAll || isLastPage) {
        onProgress?.(pageNumber, pageNumber);
        break;
      }

      onProgress?.(pageNumber, totalPages || Math.max(1, Math.ceil(apiTotalCount / pageSize)));
      pageNumber += 1;
    }

    const filtered = filterChequesPorImporte(allItems, importeDesde, importeHasta);
    return {
      items: filtered,
      apiTotalCount: filtered.length !== allItems.length ? filtered.length : apiTotalCount,
      rawFetchedCount: allItems.length,
    };
  };

  const mapChequeToExportRow = (c: ChequePropio) => ({
    'Nro. Cheque': formatNroChequeExcel(c.nroCheque),
    'Banco': c.nombreBanco?.trim() || '',
    'Sucursal': c.nombreSucursal?.trim() || '',
    'Nro. Librador': excelTexto(c.nroLibrador),
    'Librador': c.librador?.trim() || '',
    'Importe': c.importe || 0,
    'Fecha Ingreso': formatDate(c.fechaIngreso),
    'Fecha Vencimiento': formatDate(c.fechaVencimiento),
    'Nro. Cuenta': c.nroCuenta?.trim() || '',
    'Concepto': c.concepto?.trim() || '',
  });

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });

      const { items, apiTotalCount, rawFetchedCount } = await fetchExportItems((current, total) => {
        setExportProgress({
          message:
            total > 0
              ? `Obteniendo datos (${current} de ${total} páginas)...`
              : 'Obteniendo datos...',
          currentPage: current,
          totalPages: total,
        });
      });

      if (items.length === 0) {
        enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
        return;
      }
      if (
        importeDesde.trim() === '' &&
        importeHasta.trim() === '' &&
        apiTotalCount > 0 &&
        rawFetchedCount < apiTotalCount
      ) {
        enqueueSnackbar(
          `Se exportaron ${items.length} de ${apiTotalCount} registros del servidor. Revise los filtros o intente nuevamente.`,
          { variant: 'warning' }
        );
      }

      setExportProgress({ message: 'Generando archivo Excel...', currentPage: 0, totalPages: 0 });

      const datos = items.map(mapChequeToExportRow);
      const ws = XLSX.utils.json_to_sheet(datos);
      aplicarColumnasTextoExcel(ws, ['Nro. Cheque', 'Nro. Librador', 'Nro. Cuenta']);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cheques Propios');
      const fileName = `Cheques_Propios_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar(`Exportado correctamente (${items.length} registros)`, { variant: 'success' });
    } catch (error: any) {
      console.error('Error al exportar:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error al exportar a Excel', { variant: 'error' });
    } finally {
      setExportingExcel(false);
      resetExportProgress();
    }
  };

  const handleExportPdf = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });

      const { items, apiTotalCount, rawFetchedCount } = await fetchExportItems((current, total) => {
        setExportProgress({
          message:
            total > 0
              ? `Obteniendo datos (${current} de ${total} páginas)...`
              : 'Obteniendo datos...',
          currentPage: current,
          totalPages: total,
        });
      });

      if (items.length === 0) {
        enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
        return;
      }
      if (
        importeDesde.trim() === '' &&
        importeHasta.trim() === '' &&
        apiTotalCount > 0 &&
        rawFetchedCount < apiTotalCount
      ) {
        enqueueSnackbar(
          `Se exportaron ${items.length} de ${apiTotalCount} registros del servidor. Revise los filtros o intente nuevamente.`,
          { variant: 'warning' }
        );
      }

      setExportProgress({ message: 'Generando archivo PDF...', currentPage: 0, totalPages: 0 });

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 36;
      const marginY = 44;
      let currentY = marginY;
      const totalImporte = items.reduce((sum, c) => sum + (c.importe || 0), 0);

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Cheques Propios', marginX, currentY);
      currentY += 22;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 12;
      doc.text(`Registros: ${items.length}  ·  Total: ${formatCurrency(totalImporte)}`, marginX, currentY);
      currentY += 12;

      if (carteraNombre.trim()) {
        doc.text(`Cartera: ${carteraNombre.trim()}`, marginX, currentY);
        currentY += 12;
      }
      if (searchTerm.trim()) {
        doc.text(`Búsqueda: ${searchTerm.trim()}`, marginX, currentY);
        currentY += 12;
      }

      currentY += 6;

      const tableData = items.map((c) => [
        formatNroChequeExcel(c.nroCheque),
        c.nombreBanco?.trim() || '',
        c.nombreSucursal?.trim() || '',
        excelTexto(c.nroLibrador),
        c.librador?.trim() || '',
        formatCurrency(c.importe || 0),
        formatDate(c.fechaIngreso),
        formatDate(c.fechaVencimiento),
        c.nroCuenta?.trim() || '',
        c.concepto?.trim() || '',
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [[
          'Nro. Cheque',
          'Banco',
          'Sucursal',
          'Nro. Librador',
          'Librador',
          'Importe',
          'F. Ingreso',
          'F. Venc.',
          'Nro. Cuenta',
          'Concepto',
        ]],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 6, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 6.5 },
        columnStyles: {
          5: { halign: 'right' },
        },
      });

      const pdfBlob = doc.output('blob');
      const safeBlob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al generar PDF:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error al generar el PDF', { variant: 'error' });
    } finally {
      setExportingPdf(false);
      resetExportProgress();
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ flex: 1, minWidth: 0 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/tesoreria/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 600, color: '#1976d2', wordBreak: 'break-word' }}
              >
                Cheques Propios
              </Typography>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcel()}
                disabled={loading || exportingExcel || exportingPdf || totalCount === 0}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                sx={{ textTransform: 'none' }}
              >
                {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handleExportPdf()}
                disabled={loading || exportingExcel || exportingPdf || totalCount === 0}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                sx={{ textTransform: 'none' }}
              >
                {exportingPdf ? 'Generando PDF…' : 'Imprimir PDF'}
              </Button>
            </Stack>
          </Stack>

          {/* Búsqueda */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por librador, banco, número de cheque..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 500 }}
            />
          </Box>

          {/* Filtros Avanzados */}
          <Accordion sx={{ mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: '#f9f9f9' }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterAltIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ color: '#1976d2' }}>
                  Filtros Avanzados
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Cartera"
                    value={carteraNombre}
                    placeholder="Seleccionar cartera..."
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setCarteraSearchOpen(true)}
                            size="small"
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    onClick={() => setCarteraSearchOpen(true)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Sucursal"
                    size="small"
                    allowEmpty
                    emptyOptionLabel="Todas"
                    value={nroSucursal || null}
                    onChange={(value) => {
                      setNroSucursal(value ?? '');
                      setPage(0);
                    }}
                    disabled={!nroBanco}
                    options={sucursalesFiltradas.map((sucursal) => ({
                      value: sucursal.nroSucursal.toString(),
                      label: sucursal.sucursal,
                    }))}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Venc. Desde"
                    value={fechaVencimientoDesde}
                    onChange={(e) => {
                      setFechaVencimientoDesde(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Venc. Hasta"
                    value={fechaVencimientoHasta}
                    onChange={(e) => {
                      setFechaVencimientoHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Ingreso Desde"
                    value={fechaIngresoDesde}
                    onChange={(e) => {
                      setFechaIngresoDesde(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Ingreso Hasta"
                    value={fechaIngresoHasta}
                    onChange={(e) => {
                      setFechaIngresoHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Monto Desde"
                    value={importeDesde}
                    onChange={(e) => {
                      setImporteDesde(e.target.value);
                      setPage(0);
                    }}
                    placeholder="0"
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Monto Hasta"
                    value={importeHasta}
                    onChange={(e) => {
                      setImporteHasta(e.target.value);
                      setPage(0);
                    }}
                    placeholder="0"
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    size="small"
                  >
                    Limpiar Filtros
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Tabla (desktop) / Cards (mobile) */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {cheques.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  {searchTerm
                    ? 'No se encontraron cheques con los criterios de búsqueda'
                    : 'No hay cheques propios registrados'}
                </Typography>
              ) : (
                cheques.map((cheque, index) => (
                  <Card
                    key={index}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Cheque #{formatNroChequeVista(cheque.nroCheque)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {cheque.nombreBanco?.trim() || '-'}
                            {cheque.nombreSucursal?.trim() ? ` · ${cheque.nombreSucursal.trim()}` : ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Librador:</strong>{' '}
                          {cheque.librador?.trim() || '-'}
                          {cheque.nroLibrador ? ` (#${cheque.nroLibrador})` : ''}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {formatCurrency(cheque.importe || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Ingreso:</strong> {formatDate(cheque.fechaIngreso)}
                          {' · '}
                          <strong>Venc.:</strong> {formatDate(cheque.fechaVencimiento)}
                        </Typography>
                        {(cheque.nroCuenta?.trim() || cheque.concepto?.trim()) && (
                          <Typography variant="body2" color="text.secondary">
                            {cheque.nroCuenta?.trim() ? (
                              <>
                                <strong>Cuenta:</strong> {cheque.nroCuenta.trim()}
                              </>
                            ) : null}
                            {cheque.nroCuenta?.trim() && cheque.concepto?.trim() ? ' · ' : null}
                            {cheque.concepto?.trim() ? (
                              <>
                                <strong>Concepto:</strong> {cheque.concepto.trim()}
                              </>
                            ) : null}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1,
                  },
                }}
              />
            </Stack>
          ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
              <>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'nroCheque' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nroCheque'}
                          direction={orderBy === 'nroCheque' ? order : 'asc'}
                          onClick={() => handleRequestSort('nroCheque')}
                        >
                          Nro. Cheque
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'nombreBanco' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nombreBanco'}
                          direction={orderBy === 'nombreBanco' ? order : 'asc'}
                          onClick={() => handleRequestSort('nombreBanco')}
                        >
                          Banco
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'nombreSucursal' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nombreSucursal'}
                          direction={orderBy === 'nombreSucursal' ? order : 'asc'}
                          onClick={() => handleRequestSort('nombreSucursal')}
                        >
                          Sucursal
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'nroLibrador' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nroLibrador'}
                          direction={orderBy === 'nroLibrador' ? order : 'asc'}
                          onClick={() => handleRequestSort('nroLibrador')}
                        >
                          Nro. Librador
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'librador' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'librador'}
                          direction={orderBy === 'librador' ? order : 'asc'}
                          onClick={() => handleRequestSort('librador')}
                        >
                          Librador
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'importe' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'importe'}
                          direction={orderBy === 'importe' ? order : 'asc'}
                          onClick={() => handleRequestSort('importe')}
                        >
                          Importe
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'fechaIngreso' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'fechaIngreso'}
                          direction={orderBy === 'fechaIngreso' ? order : 'asc'}
                          onClick={() => handleRequestSort('fechaIngreso')}
                        >
                          Fecha Ingreso
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'fechaVencimiento' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'fechaVencimiento'}
                          direction={orderBy === 'fechaVencimiento' ? order : 'asc'}
                          onClick={() => handleRequestSort('fechaVencimiento')}
                        >
                          Fecha Vencimiento
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'nroCuenta' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nroCuenta'}
                          direction={orderBy === 'nroCuenta' ? order : 'asc'}
                          onClick={() => handleRequestSort('nroCuenta')}
                        >
                          Nro. Cuenta
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cheques.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {searchTerm ? 'No se encontraron cheques con los criterios de búsqueda' : 'No hay cheques propios registrados'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cheques.map((cheque, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{formatNroChequeVista(cheque.nroCheque)}</TableCell>
                          <TableCell>{cheque.nombreBanco?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.nombreSucursal?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.nroLibrador || '-'}</TableCell>
                          <TableCell>{cheque.librador?.trim() || '-'}</TableCell>
                          <TableCell>{formatCurrency(cheque.importe || 0)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaIngreso)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaVencimiento)}</TableCell>
                          <TableCell>{cheque.nroCuenta?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.concepto?.trim() || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`}
                />
              </>
          </TableContainer>
          )}
        </CardContent>
      </Card>

      <CarteraSearch
        open={carteraSearchOpen}
        onClose={() => setCarteraSearchOpen(false)}
        onSelect={handleCarteraSelect}
      />

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Cheques Propios"
          documentId="cheques-propios"
          documentType="informeCliente"
          downloadFileName={`Cheques_Propios_${new Date().toISOString().split('T')[0]}.pdf`}
        />
      ) : null}

      <ExportProgressDialog
        open={exportingExcel || exportingPdf}
        progress={exportProgress}
      />
    </Box>
  );
};

export default ChequesPropios;

